import { debounce, map } from 'lodash'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { defaultMemoize } from 'reselect'

import { AutoComplete } from '@/components/Form'
import { Person } from '@/components/Icons'
import { FETCH_SPACE_MEMBERS_SEARCH } from '@/pages/SpaceMemberEditPage/actionTypes'
import { getSearchedNewUsersNotInCurrentSpace } from '@/pages/SpaceMembersPage/selectors'
import { fetchUsersPaginated } from '@/store/actions'
import { isRequestPending } from '@/store/selectors'

import messages from './messages'

import './index.scss'

import { getCurrentSpaceId } from '@/components/App/selectors'
import { USER_ATTRIBUTES } from '@/components/User'

const SEARCH_DELAY_MS = 300

function UserSearch(props: any) {
  const intl = useIntl()

  const [state, setState] = useState({
    filter: ''
  })

  const fetchUsersNotInSpace = debounce(filter => {
    const query: { attributes: string[]; count: number; spaceId: any; notInSpace: boolean; filter?: string } = {
      attributes: USER_ATTRIBUTES,
      count: 5,
      spaceId: props.currentSpaceId,
      notInSpace: true
    }

    if (filter) {
      query.filter = USER_ATTRIBUTES.map(attribute => `${attribute} co ${JSON.stringify(filter)}`).join(' or ')
    }

    props.actions.fetchUsersPaginated(query, FETCH_SPACE_MEMBERS_SEARCH)
  }, SEARCH_DELAY_MS)

  const generateSearchResults = defaultMemoize((searchTerm, users) => {
    if (!searchTerm?.length) {
      return []
    }

    return map(users, user => ({
      label: user.displayName,
      leftIcon: <Person />,
      subLabel: user.email,
      value: user.id,
      id: user.id
    }))
  })

  const onChange = (filter: any) => {
    setState({
      filter: filter
    })

    fetchUsersNotInSpace(filter)
  }

  const onSelect = (val: any) => {
    setState({
      filter: val.label
    })
    props.onOptionSelected(props.users.find((user: any) => user.id === val.id))
    return { display: val.label, value: val.value }
  }

  const renderItem = ({ label, leftIcon, subLabel, value }: any) => {
    return (
      <div key={value} className="space-member-result">
        {!!leftIcon && <div className="result__left">{leftIcon}</div>}
        <div className="result__right">
          <p>{label}</p>
          {!!subLabel && <p>{subLabel}</p>}
        </div>
      </div>
    )
  }

  return (
    <AutoComplete
      className="user-search_autocomplete"
      key="user"
      name="userSearch"
      label={<FormattedMessage {...messages.inputLabel} />}
      placeholder={intl.formatMessage(messages.inputPlaceholder)}
      onResultClick={onSelect}
      onSearchChange={onChange}
      renderResult={renderItem}
      isSearching={props.isSearchingUsers}
      results={generateSearchResults(state.filter, props.users)}
    />
  )
}

UserSearch.displayName = 'UserSearch'

UserSearch.propTypes = {
  actions: PropTypes.shape({
    fetchUsersPaginated: PropTypes.func.isRequired
  }).isRequired,

  isSearchingUsers: PropTypes.bool.isRequired,
  users: PropTypes.arrayOf(PropTypes.object),
  currentSpaceId: PropTypes.string.isRequired,
  onOptionSelected: PropTypes.func.isRequired
}

UserSearch.defaultProps = {
  users: []
}

const DisconnectedUserSearch = UserSearch
export { /** @deprecated */ DisconnectedUserSearch as UserSearch }

function mapStateToProps(state: any) {
  return {
    users: getSearchedNewUsersNotInCurrentSpace(state),
    isSearchingUsers: isRequestPending(state, FETCH_SPACE_MEMBERS_SEARCH),
    currentSpaceId: getCurrentSpaceId(state)
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators({ fetchUsersPaginated }, dispatch)
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DisconnectedUserSearch)
